import React, { useCallback, useRef, useState } from 'react';

/** Smallest a box may be squashed to, as a fraction of the frame. */
const MIN_SIZE = 0.02;
/** How much of a box must stay inside the frame when it is dragged off an edge. */
const MIN_VISIBLE = 0.05;
/** Corners first, then edges — the order they are drawn in. */
const RESIZE_HANDLES = ['tl', 'tr', 'bl', 'br', 't', 'b', 'l', 'r'] as const;

interface DraggableOverlayProps {
  x: number;         // normalized 0–1
  y: number;         // normalized 0–1
  scale: number;
  /**
   * Box size as a fraction of the frame. When given, the wrapper carries the size itself so the
   * percentages resolve against the video rect — a child sized in % against an auto-width
   * absolutely-positioned parent resolves against that parent's shrink-to-fit width instead,
   * which both misplaces the box and makes it breathe as it is dragged towards an edge.
   * Overlays that size themselves (emoji) leave these unset and use `scale`.
   */
  width?: number;
  height?: number;
  rotation?: number; // degrees
  opacity?: number;
  selected: boolean;
  onSelect: () => void;
  onMove: (x: number, y: number) => void;
  onScale: (scale: number) => void;
  /**
   * Resize for a box sized by `width`/`height`. All four values are absolute, not factors —
   * the centre moves because the *opposite* corner is what stays pinned.
   */
  onResize?: (width: number, height: number, x: number, y: number) => void;
  onRotate?: (rotation: number) => void;
  onDelete?: () => void;
  children: React.ReactNode;
}

const DraggableOverlay: React.FC<DraggableOverlayProps> = ({
  x, y, scale, width, height, rotation = 0, opacity = 1, selected,
  onSelect, onMove, onScale, onResize, onRotate, onDelete, children,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const sized = width != null && height != null;

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect();
    setDragging(true);
    const parent = ref.current?.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startNX = x;
    const startNY = y;

    // A sized box may hang off an edge as long as a sliver stays on screen — bleeding a banner
    // past the bottom is ordinary, and pinning the *centre* inside the frame made the box stop
    // following the cursor well before the edge, which reads as the drag landing somewhere else.
    // An overlay that carries no size (emoji) has no edges to reason about, so it keeps the
    // old centre clamp.
    const halfW = sized ? width! / 2 : 0;
    const halfH = sized ? height! / 2 : 0;
    const loX = sized ? -halfW + MIN_VISIBLE : 0;
    const hiX = sized ? 1 + halfW - MIN_VISIBLE : 1;
    const loY = sized ? -halfH + MIN_VISIBLE : 0;
    const hiY = sized ? 1 + halfH - MIN_VISIBLE : 1;

    const onMouseMove = (ev: MouseEvent) => {
      const dx = (ev.clientX - startX) / rect.width;
      const dy = (ev.clientY - startY) / rect.height;
      onMove(
        Math.max(loX, Math.min(hiX, startNX + dx)),
        Math.max(loY, Math.min(hiY, startNY + dy)),
      );
    };
    const onMouseUp = () => {
      setDragging(false);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [x, y, sized, width, height, onSelect, onMove]);

  const handleScaleMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const startY = e.clientY;
    const startScale = scale;
    const onMouseMove = (ev: MouseEvent) => {
      const dy = startY - ev.clientY;
      onScale(Math.max(0.3, Math.min(5, startScale + dy * 0.01)));
    };
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [scale, onScale]);

  /**
   * Resize a sized box from any of its eight handles. Each handle moves only the edges it names
   * and leaves the rest pinned, so width and height move independently — the way to stretch an
   * image across the bottom of the frame is to pull the side handles out and the top one down.
   * Nothing preserves aspect; squashing is the point.
   *
   * The model stores a centre and a size, so the edges are recovered, moved, and folded back.
   */
  const handleResizeMouseDown = useCallback((e: React.MouseEvent, handle: string) => {
    if (!onResize || width == null || height == null) return;
    e.stopPropagation();
    e.preventDefault();
    const parent = ref.current?.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    // Edges at the start of the gesture; the ones this handle does not name never move.
    const l0 = x - width / 2;
    const r0 = x + width / 2;
    const t0 = y - height / 2;
    const b0 = y + height / 2;

    const onMouseMove = (ev: MouseEvent) => {
      const dx = (ev.clientX - startX) / rect.width;
      const dy = (ev.clientY - startY) / rect.height;
      let l = l0, r = r0, t = t0, b = b0;
      // Each edge is stopped MIN_SIZE short of its opposite rather than allowed to cross it.
      if (handle.includes('l')) l = Math.min(l0 + dx, r0 - MIN_SIZE);
      if (handle.includes('r')) r = Math.max(r0 + dx, l0 + MIN_SIZE);
      if (handle.includes('t')) t = Math.min(t0 + dy, b0 - MIN_SIZE);
      if (handle.includes('b')) b = Math.max(b0 + dy, t0 + MIN_SIZE);
      onResize(r - l, b - t, (l + r) / 2, (t + b) / 2);
    };
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [x, y, width, height, onResize]);

  const handleRotateMouseDown = useCallback((e: React.MouseEvent) => {
    if (!onRotate) return;
    e.stopPropagation();
    e.preventDefault();
    const el = ref.current;
    if (!el) return;
    const parent = el.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    const cx = rect.left + x * rect.width;
    const cy = rect.top + y * rect.height;

    const onMouseMove = (ev: MouseEvent) => {
      const angle = Math.atan2(ev.clientY - cy, ev.clientX - cx) * (180 / Math.PI);
      onRotate(Math.round(angle));
    };
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [x, y, onRotate]);

  return (
    <div
      ref={ref}
      className={`draggable-overlay ${selected ? 'selected' : ''} ${dragging ? 'dragging' : ''}`}
      style={{
        left: `${x * 100}%`,
        top: `${y * 100}%`,
        ...(sized ? { width: `${width! * 100}%`, height: `${height! * 100}%` } : {}),
        // A sized box carries its own dimensions, so nothing is left for `scale` to do.
        transform: `translate(-50%, -50%)${sized ? '' : ` scale(${scale})`} rotate(${rotation}deg)`,
        opacity,
        zIndex: selected ? 100 : 10,
      }}
      onMouseDown={handleMouseDown}
      // `click` is its own event and bubbles even though mousedown was stopped — without this
      // it reaches the frame's deselect handler, and the overlay is unselected by the very
      // click that selected it, taking its handles with it.
      onClick={(e) => e.stopPropagation()}
    >
      {children}
      {selected && (
        <>
          {sized ? RESIZE_HANDLES.map(h => (
            <div
              key={h}
              className={`overlay-handle overlay-handle-${h}`}
              onMouseDown={(e) => handleResizeMouseDown(e, h)}
              title="Resize"
            />
          )) : (
            <div
              className="overlay-handle overlay-handle-scale"
              onMouseDown={handleScaleMouseDown}
              title="Resize"
            />
          )}
          {onRotate && (
            <div className="overlay-handle overlay-handle-rotate" onMouseDown={handleRotateMouseDown} title="Rotate" />
          )}
          {onDelete && (
            <button className="overlay-delete-btn" onClick={(e) => { e.stopPropagation(); onDelete(); }}>✕</button>
          )}
        </>
      )}
    </div>
  );
};

export default DraggableOverlay;
